import { useMemo } from 'react';
import { NavigationItem } from '@/hooks/useNavigationPreferences';
import { COMPLETE_MENU_STRUCTURE, MenuNode } from '@/config/menuStructure';

// Local implementation of flattenMenuStructure
const flattenMenuStructure = (nodes: MenuNode[], parentPath = ""): any[] => {
  const routes: any[] = [];
  nodes.forEach((node) => {
    const currentPath = parentPath ? `${parentPath} > ${node.label}` : node.label;
    if (node.path) {
      const category = parentPath ? parentPath.split(' > ')[0] : node.label;
      routes.push({
        id: node.id, // Include the menu node id for permission mapping
        label: node.label,
        path: node.path,
        category: category,
        fullPath: currentPath
      });
    }
    if (node.children) {
      routes.push(...flattenMenuStructure(node.children, currentPath));
    }
  });
  return routes;
};

export const useMenuStructureNavigation = () => {
  // Convert menu structure to navigation items
  const defaultNavigationItems = useMemo((): NavigationItem[] => {
    const flattened = flattenMenuStructure(COMPLETE_MENU_STRUCTURE);
    
    return flattened.map((route, index) => ({
      id: route.id || route.fullPath || route.path, // Use menu node id first for permission mapping
      label: route.label,
      path: route.path,
      icon: route.icon,
      visible: true,
      order: index,
      category: route.category,
      fullPath: route.fullPath, // Preserve the hierarchical label path
    }));
  }, []);

  // Create a function to merge user preferences with default structure
  const mergeWithPreferences = (userPreferences: NavigationItem[] | null): NavigationItem[] => {
    if (!userPreferences || userPreferences.length === 0) {
      return defaultNavigationItems;
    }

    // Detect if userPreferences are permission slugs (id-only) vs full nav items
    const hasPathInPrefs = userPreferences.some(item => item.path);
    
    if (!hasPathInPrefs) {
      // These are permission slugs from custom_roles.permissions
      // Build a set of allowed IDs (case-insensitive)
      const allowedIds = new Set(
        userPreferences.map(item => (item.id || '').toLowerCase())
      );

      // Keep the default order; only toggle visibility based on permissions
      const result = defaultNavigationItems.map((defaultItem, index) => {
        const idLower = (defaultItem.id || '').toLowerCase();
        const visibleByPermission = allowedIds.has(idLower);
        return {
          ...defaultItem,
          visible: visibleByPermission,
          order: index,
        };
      });
      return result;
    }

    // Standard merge: userPreferences are full navigation items
    // Create a map of user preferences by path
    const userPrefsMap = new Map(userPreferences.map(item => [item.path, item]));
    
    // Check if preferences came from role-based filtering (small subset = role-filtered)
    // If the preferences list is much smaller than defaults, treat missing items as hidden
    const isRoleFiltered = userPreferences.length < defaultNavigationItems.length * 0.8;
    
    // Merge default items with user preferences
    // Always use label/fullPath from defaults (menuStructure is source of truth)
    const merged = defaultNavigationItems.map(defaultItem => {
      const userPref = userPrefsMap.get(defaultItem.path);
      if (userPref) {
        // Keep user's visibility/order, but always use current label from menuStructure
        return { 
          ...defaultItem, 
          visible: userPref.visible, 
          order: userPref.order 
        };
      }
      // If role-filtered, items not in preferences should be hidden
      if (isRoleFiltered) {
        return { ...defaultItem, visible: false };
      }
      return defaultItem;
    });
    
    // Sort by order within categories
    const sorted = merged.sort((a, b) => {
      if (a.category !== b.category) {
      return (a.category || '').localeCompare(b.category || '');
      }
      return a.order - b.order;
    });
    return sorted;
  };

  return {
    defaultNavigationItems,
    mergeWithPreferences,
  };
};